import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Trophy, Award, Loader2, ChevronUp } from 'lucide-react';
import { motion } from 'framer-motion';

// Skill Contest Leaderboard (doc Point 14). Cream/gold theme matching the reference:
// State Rank card + "Away to qualify" card + State/Regional/National tabs + ranked list
// with the current learner highlighted.
const CREAM = '#FBF4DA';
const GOLD = '#F3CE45';
const INK = '#2B2400';

const TABS = [
    { key: 'state', label: 'State' },
    { key: 'regional', label: 'Regional' },
    { key: 'national', label: 'National' },
];
const SCOPE_LABEL = { state: 'State', regional: 'Regional', national: 'National' };

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');

export default function Leaderboard() {
    const [scope, setScope] = useState('national');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cache, setCache] = useState({});

    useEffect(() => {
        let alive = true;
        if (cache[scope]) { setData(cache[scope]); setLoading(false); return; }
        setLoading(true);
        api.get(`/trainee/leaderboard?scope=${scope}`)
            .then((r) => { if (!alive) return; setData(r.data); setCache((c) => ({ ...c, [scope]: r.data })); })
            .catch(() => { if (alive) setData({ top: [], me: null, my_rank: null, away_to_qualify: 0 }); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps

    const top = data?.top || [];
    const me = data?.me;
    const meInTop = me && top.some((e) => e.is_me);

    return (
        <div data-testid="leaderboard-page" className="max-w-xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-[28px] overflow-hidden shadow-[0_12px_40px_rgba(0,0,0,0.25)]"
                style={{ background: CREAM }}>

                {/* Title */}
                <div className="px-5 pt-5 pb-4 flex items-center gap-2.5">
                    <span className="grid place-items-center w-9 h-9 rounded-xl" style={{ background: GOLD }}>
                        <Trophy className="w-5 h-5" style={{ color: INK }} />
                    </span>
                    <h1 className="font-display font-extrabold text-xl tracking-tight" style={{ color: INK }}>
                        Skill Contest Leaderboard
                    </h1>
                </div>

                {/* Rank + qualify cards */}
                <div className="px-5 grid grid-cols-2 gap-3">
                    <div data-testid="lb-rank-card" className="rounded-2xl px-4 py-3.5" style={{ background: GOLD }}>
                        <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide" style={{ color: '#6B5A12' }}>
                            <Award className="w-3.5 h-3.5" /> {SCOPE_LABEL[scope]} Rank
                        </div>
                        <div className="font-display font-black text-3xl mt-0.5" style={{ color: INK }} data-testid="lb-my-rank">
                            {data?.my_rank ? `#${data.my_rank}` : '—'}
                        </div>
                    </div>
                    <div data-testid="lb-away-card" className="rounded-2xl px-4 py-3.5 bg-white" style={{ boxShadow: 'inset 0 0 0 1.5px #EAD9A0' }}>
                        <div className="font-display font-black text-3xl leading-none" style={{ color: INK }} data-testid="lb-away">
                            {data?.away_to_qualify ? fmt(data.away_to_qualify) : 0}
                        </div>
                        <div className="text-[12px] font-semibold mt-1 inline-flex items-center gap-1" style={{ color: '#8A7A3E' }}>
                            <ChevronUp className="w-3.5 h-3.5" /> Away to qualify
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="px-5 mt-4 flex gap-7 border-b" style={{ borderColor: '#EADFB6' }}>
                    {TABS.map((t) => {
                        const active = scope === t.key;
                        return (
                            <button key={t.key} onClick={() => setScope(t.key)} data-testid={`lb-tab-${t.key}`}
                                className="relative pb-2.5 text-sm font-bold transition-colors"
                                style={{ color: active ? INK : '#A99C6A' }}>
                                {t.label}
                                {active && <span className="absolute left-0 right-0 -bottom-[1.5px] h-[3px] rounded-full" style={{ background: INK }} />}
                            </button>
                        );
                    })}
                </div>

                {/* List */}
                <div className="px-3 py-3 min-h-[220px]">
                    {loading ? (
                        <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: INK }} /></div>
                    ) : top.length === 0 ? (
                        <div className="py-16 text-center text-sm font-medium" style={{ color: '#8A7A3E' }}>No learners in this list yet.</div>
                    ) : (
                        <div className="space-y-1.5">
                            {top.map((e) => <Row key={e.user_id || e.rank} e={e} />)}
                        </div>
                    )}

                    {/* Pinned "You" row when the learner isn't already in the visible top list */}
                    {!loading && me && !meInTop && (
                        <div className="mt-2 pt-2 border-t border-dashed" style={{ borderColor: '#EADFB6' }}>
                            <Row e={me} pinned />
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
    );
}

function Row({ e, pinned }) {
    const isMe = e.is_me;
    return (
        <div
            data-testid={isMe ? 'lb-my-row' : `lb-row-${e.rank}`}
            className="flex items-center gap-3 rounded-2xl px-3 py-2.5"
            style={isMe
                ? { background: '#FBE7A0', boxShadow: 'inset 0 0 0 1.5px #E9C94B' }
                : { background: '#FFFFFF' }}
        >
            <div className="w-9 text-center font-display font-black text-sm shrink-0"
                style={{ color: e.rank && e.rank <= 3 ? '#B8860B' : '#6B5A12' }}>
                {e.rank ? `#${e.rank}` : '—'}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                    <span className="font-bold text-[15px] truncate" style={{ color: INK }}>{e.name}</span>
                    {isMe && <span className="text-[10px] font-black px-1.5 py-0.5 rounded-md shrink-0" style={{ background: GOLD, color: INK }}>You</span>}
                </div>
                {e.city && <div className="text-[12px] truncate" style={{ color: '#A0925C' }}>{e.city}</div>}
            </div>
            <div className="font-display font-black text-[15px] shrink-0" style={{ color: INK }}>
                {fmt(e.points)} <span className="text-[11px] font-bold" style={{ color: '#A0925C' }}>pts</span>
            </div>
        </div>
    );
}
